import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { ActionCorpusFile } from '@/types';

interface FileMultiSelectProps {
  files: ActionCorpusFile[];
  selectedFiles: string[];
  onChange: (selectedFiles: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  className?: string;
}

export function FileMultiSelect({
  files,
  selectedFiles,
  onChange,
  disabled = false,
  error = false,
  placeholder = 'Select warrant files',
  className,
}: FileMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange(files.map(file => file.name));
    }
  };

  const handleFileToggle = (fileName: string) => {
    if (selectedFiles.includes(fileName)) {
      onChange(selectedFiles.filter(f => f !== fileName));
    } else {
      onChange([...selectedFiles, fileName]);
    }
  };

  const handleRemoveFile = (fileName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedFiles.filter(f => f !== fileName));
  };

  const getDisplayText = () => {
    if (selectedFiles.length === 0) {
      return placeholder;
    }
    if (selectedFiles.length === files.length) {
      return `All files selected (${files.length})`;
    }
    if (selectedFiles.length === 1) {
      const file = files.find(f => f.name === selectedFiles[0]);
      return file?.displayName || selectedFiles[0];
    }
    return `${selectedFiles.length} files selected`;
  };

  const isAllSelected = files.length > 0 && selectedFiles.length === files.length;
  const isSomeSelected = selectedFiles.length > 0 && selectedFiles.length < files.length;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus:ring-destructive",
          isOpen && "ring-2 ring-ring ring-offset-2"
        )}
      >
        <span className={cn(
          "truncate",
          selectedFiles.length === 0 && "text-muted-foreground"
        )}>
          {getDisplayText()}
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 opacity-50 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Selected Files Tags */}
      {selectedFiles.length > 0 && selectedFiles.length <= 3 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedFiles.map(fileName => {
            const file = files.find(f => f.name === fileName);
            return (
              <div
                key={fileName}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-md"
              >
                <span className="truncate max-w-[200px]">
                  {file?.displayName || fileName}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveFile(fileName, e)}
                  disabled={disabled}
                  className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-[300px] overflow-hidden">
          {/* Select All Option */}
          <div className="border-b border-border">
            <label
              className={cn(
                "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent",
                "transition-colors"
              )}
            >
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
                className={cn(
                  isSomeSelected && "data-[state=checked]:bg-primary/50"
                )}
              />
              <span className="text-sm font-medium">
                Select All ({files.length})
              </span>
            </label>
          </div>

          {/* File List */}
          <div className="overflow-y-auto max-h-[250px]">
            {files.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                No files available
              </div>
            ) : (
              files.map((file) => {
                const isSelected = selectedFiles.includes(file.name);
                return (
                  <label
                    key={file.id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent",
                      "transition-colors",
                      isSelected && "bg-accent/50"
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleFileToggle(file.name)}
                    />
                    <span className="text-sm flex-1 truncate">
                      {file.displayName}
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
